import React from 'react';
import {
    Alert,
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner
} from 'reactstrap';
import {useQuery} from 'react-query';
import {ContentSectionType, ProjectDataIndex} from '../../api/types.ts';
import useGetJournalEntries from '../../api/journal/useGetJournalEntries.ts';
import DateTimeLocalDisplay from '../shared/DateTimeLocalDisplay.tsx';

interface VibeCodeHistoryModalProps {
    isOpen: boolean;
    projectId: string;
    dataIndex: ProjectDataIndex;
    onClose: () => void;
}

const historyPrefix = 'Vibe Code Prompt:';
const take = 200;

const VibeCodeHistoryModal: React.FC<VibeCodeHistoryModalProps> = ({
    isOpen,
    projectId,
    dataIndex,
    onClose
}) => {
    const getJournalEntries = useGetJournalEntries({
        mixId: projectId,
        skipDate: null,
        take
    });
    const {data, isError, isLoading} = useQuery(
        ['vibeCodePromptHistory', projectId],
        getJournalEntries,
        {
            enabled: isOpen,
            refetchOnMount: 'always'
        }
    );

    const tabSuffix = `(${ProjectDataIndex[dataIndex]})`;
    const history = (data ?? []).flatMap(entry => {
        const header = entry.content.sections.find(
            section =>
                section.type === ContentSectionType.Header &&
                section.content?.startsWith(historyPrefix) &&
                section.content.endsWith(tabSuffix)
        );
        const prompt = entry.content.sections.find(
            section => section.type === ContentSectionType.Code
        );

        return header && prompt?.content
            ? [
                  {
                      id: entry.id,
                      title: header.content,
                      prompt: prompt.content,
                      createdOnUtc: entry.createdOnUtc
                  }
              ]
            : [];
    });

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg" scrollable>
            <ModalHeader toggle={onClose}>Vibe prompt history</ModalHeader>
            <ModalBody>
                {isLoading && (
                    <div className="d-flex justify-content-center p-4">
                        <Spinner color="primary" />
                    </div>
                )}
                {isError && (
                    <Alert color="danger">Unable to load prompt history.</Alert>
                )}
                {!isLoading && !isError && history.length === 0 && (
                    <p className="text-muted mb-0">
                        No saved Vibe Code prompts yet.
                    </p>
                )}
                {history.map(item => (
                    <article className="vibe-history-entry" key={item.id}>
                        <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                            <strong>{item.title}</strong>
                            <small className="text-muted">
                                <DateTimeLocalDisplay
                                    dateTimeUtc={item.createdOnUtc}
                                    format="dd MMM yyyy, h:mma"
                                />
                            </small>
                        </div>
                        <pre className="mb-0">{item.prompt}</pre>
                    </article>
                ))}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onClose}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default VibeCodeHistoryModal;
